import React, { useCallback, useEffect, useRef, useState } from "react";

import { expandLabelData } from "@organisms/PressLabelOptionWindow/PressLabelOptionWindow.data";

import * as S from "./PressLabelElement.style";

import type { LabelCode } from "@/types/code.types";

interface Props {
  children: React.ReactNode;
  entity: LabelCode;
  handleClick?: React.MouseEventHandler<HTMLElement>;
  columnLabelViewerWidth?: number;
  columnLabelViewerTop?: number;
  searchTerm?: string;
  isClassificationPage?: boolean;
}

const PressLabelElement = ({
  children,
  entity,
  handleClick,
  columnLabelViewerWidth,
  columnLabelViewerTop,
  searchTerm,
  isClassificationPage,
}: Props) => {
  const toolTipRef = useRef<HTMLDivElement>(null);
  const [offsetLeftLocation, setOffsetLeftLocation] = useState(0);
  const [offsetTopLocation, setOffsetTopLocation] = useState(0);
  const labelEntityData = expandLabelData.filter(
    (label) => label.entity === entity,
  )[0];
  const isOpacity = searchTerm !== "" && !isClassificationPage;

  const stopCopy = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;

    const wrapper = e.currentTarget;

    const tooltipElement = wrapper.querySelector(".tooltip");
    if (!tooltipElement) return;

    // NOTE: tooltip의 높이를 체크하여 표시 여부 확인
    const tooltipHeight = tooltipElement.getBoundingClientRect().height;
    const tooltipVisible = tooltipHeight > 0;

    if (tooltipVisible) {
      const range = selection.getRangeAt(0);

      const labelElement = wrapper.querySelector("[entity]");
      if (!labelElement) return;

      const tooltipSelected = range.intersectsNode(tooltipElement);

      if (tooltipSelected) {
        e.preventDefault();
        e.stopPropagation();

        const labelSelected = range.intersectsNode(labelElement);

        if (labelSelected) {
          // NOTE: 라벨 내 텍스트를 선택한 경우, 라벨 내 선택된 부분만 복사
          const tempRange = document.createRange();

          // NOTE: 라벨 내부에서 선택 시작점과 끝점 확인하여 추출
          if (labelElement.contains(range.startContainer)) {
            tempRange.setStart(range.startContainer, range.startOffset);
          } else {
            tempRange.setStart(labelElement, 0);
          }

          if (labelElement.contains(range.endContainer)) {
            tempRange.setEnd(range.endContainer, range.endOffset);
          } else {
            tempRange.setEnd(labelElement, labelElement.childNodes.length);
          }

          const tempSelection = window.getSelection();
          tempSelection?.removeAllRanges();
          tempSelection?.addRange(tempRange);
          const labelSelectedText = tempSelection?.toString() || "";

          tempSelection?.removeAllRanges();
          tempSelection?.addRange(range);

          e.clipboardData?.setData("text/plain", labelSelectedText);
        } else {
          e.clipboardData?.setData("text/plain", "");
        }
      }
    }
  };

  const calculateOffsetLeftLocation = useCallback(() => {
    const columnWidth = columnLabelViewerWidth ?? 0;

    return columnWidth - 200;
  }, [columnLabelViewerWidth]);

  const calculateOffsetTopLocation = useCallback(() => {
    const columnHeight = columnLabelViewerTop ?? 0;

    return columnHeight;
  }, [columnLabelViewerTop]);

  useEffect(() => {
    if (toolTipRef.current) {
      setOffsetLeftLocation(toolTipRef.current.offsetLeft);
    }
  }, [columnLabelViewerWidth, setOffsetTopLocation]);

  const currentOffsetTopLocation = () => {
    if (toolTipRef.current) {
      setOffsetTopLocation(toolTipRef.current.getBoundingClientRect().y - 35);
    }
  };

  useEffect(() => {
    if (toolTipRef.current) {
      setOffsetTopLocation(toolTipRef.current.getBoundingClientRect().y - 35);
    }
  }, []);

  return (
    <S.PressTextWrapper
      ref={toolTipRef}
      onCopy={stopCopy}
      onMouseEnter={() => currentOffsetTopLocation()}
    >
      <S.PressLabel entity={entity} onClick={handleClick} isOpacity={isOpacity}>
        {children}
      </S.PressLabel>

      {!isOpacity && labelEntityData?.text ? (
        <S.LabelTooltip
          className="tooltip"
          entity={entity}
          isEndLocation={offsetLeftLocation > calculateOffsetLeftLocation()}
          isTopLocation={offsetTopLocation - calculateOffsetTopLocation() < 0}
          $contentText={labelEntityData.text}
        />
      ) : null}
    </S.PressTextWrapper>
  );
};

export default PressLabelElement;
